import React, { useState } from 'react';
import { MailWarning, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

/** Banner for org admins / HR users whose email is unverified — digests & alerts are withheld until they verify. */
export default function EmailVerifyNudge({ email, verified, onVerified }) {
  const [stage, setStage] = useState('idle'); // idle | sent | done
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  if (verified || !email || stage === 'done') return null;

  const send = async () => {
    setBusy(true);
    try {
      const r = await apiCall('/api/auth/otp/send', { method: 'POST', body: JSON.stringify({ email }) });
      setStage('sent');
      toast.success(r.reused ? 'Your previous code is still valid — check your inbox' : `Verification code sent to ${email}`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const verify = async () => {
    if (code.trim().length < 6) return;
    setBusy(true);
    try {
      await apiCall('/api/auth/otp/verify', { method: 'POST', body: JSON.stringify({ email, otp: code.trim() }) });
      setStage('done'); toast.success('Email verified — your HR digests and alerts will now be delivered');
      onVerified?.();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="email-verify-nudge">
      <MailWarning className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">Your email <span className="font-mono">{email}</span> isn't verified</p>
        <p className="text-xs text-amber-800">Weekly HR digests, compliance alerts and document-access notices are withheld from unverified addresses. Verify once and they start arriving.</p>
      </div>
      {stage === 'idle' ? (
        <button onClick={send} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50" data-testid="email-verify-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send verification code
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="6-digit code"
            className="w-32 text-sm border border-amber-300 rounded-lg px-3 py-2 text-slate-900 tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-500/40" data-testid="email-verify-code" />
          <button onClick={verify} disabled={busy || code.length < 6} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="email-verify-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Verify
          </button>
          <button onClick={send} disabled={busy} className="text-xs text-amber-800 underline" data-testid="email-verify-resend">Resend</button>
        </div>
      )}
    </div>
  );
}
